<script setup>
import { ref, watch, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength, helpers } from '@vuelidate/validators';
import FileUpload from 'vue-upload-component';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { MEDIA_CONFIG, VALIDATION_CONSTANTS } from '../constants.js';
import { onlyOneVariable, validUrl, phoneFormat } from '../validations';
import { uploadFile } from 'dashboard/helper/uploadHelper';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

function createEmptyCard() {
  return {
    id: Date.now() + Math.random(),
    headerType: 'IMAGE',
    mediaFile: null,
    mediaUrl: '',
    isUploading: false,
    body: '',
    buttons: [],
  };
}

const cards = ref(
  props.modelValue.length > 0
    ? props.modelValue
    : [
        createEmptyCard(),
        createEmptyCard(), // Minimum 2 cards
      ]
);

function addCard() {
  if (cards.value.length < VALIDATION_CONSTANTS.MAX_CAROUSEL_CARDS) {
    cards.value.push(createEmptyCard());
  }
}

function removeCard(index) {
  if (cards.value.length > VALIDATION_CONSTANTS.MIN_CAROUSEL_CARDS) {
    cards.value.splice(index, 1);
  }
}

function addCardButton(cardIndex) {
  if (cards.value[cardIndex].buttons.length < 2) {
    cards.value[cardIndex].buttons.push({
      type: 'QUICK_REPLY',
      text: '',
      url: '',
      phone_number: '',
      example: '',
      hasVariable: false,
    });
  }
}

function removeCardButton(cardIndex, btnIndex) {
  cards.value[cardIndex].buttons.splice(btnIndex, 1);
}

async function handleCardMediaUpload(event, index) {
  const file = event?.file || null;
  if (!file) return;

  const card = cards.value[index];
  card.mediaFile = file;
  card.isUploading = true;

  try {
    const { fileUrl } = await uploadFile(file);
    card.mediaUrl = fileUrl;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Upload failed:', error);
  } finally {
    card.isUploading = false;
  }
}

const formatFileSize = bytes => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / k ** i) * 100) / 100 + ' ' + sizes[i];
};

// ===== Validations =====
const rules = computed(() => {
  return {
    cards: {
      $each: helpers.forEach({
        body: {
          required: helpers.withMessage(
            'WHATSAPP_TEMPLATES.ADD.ERRORS.VALUE_REQUIRED_ERROR',
            required
          ),
          maxLength: helpers.withMessage(
            'WHATSAPP_TEMPLATES.ADD.ERRORS.MAX_LENGTH_ERROR',
            maxLength(VALIDATION_CONSTANTS.MAX_CARD_BODY_TEXT)
          ),
        },
        mediaFile: {
          required: helpers.withMessage(
            'WHATSAPP_TEMPLATES.ADD.ERRORS.VALUE_REQUIRED_ERROR',
            required
          ),
        },
        buttons: {
          $each: helpers.forEach({
            text: {
              required: helpers.withMessage(
                'WHATSAPP_TEMPLATES.ADD.ERRORS.VALUE_REQUIRED_ERROR',
                required
              ),
              maxLength: helpers.withMessage(
                'WHATSAPP_TEMPLATES.ADD.ERRORS.BUTTONS.TEXT_MAX_LENGTH_ERROR',
                maxLength(VALIDATION_CONSTANTS.MAX_BUTTON_TEXT)
              ),
            },
            url: {
              required: helpers.withMessage(
                'WHATSAPP_TEMPLATES.ADD.ERRORS.VALUE_REQUIRED_ERROR',
                (value, button) => {
                  return button.type !== 'URL' || !!value;
                }
              ),
              validUrl,
              onlyOneVariable,
            },
            phone_number: {
              required: helpers.withMessage(
                'WHATSAPP_TEMPLATES.ADD.ERRORS.VALUE_REQUIRED_ERROR',
                (value, button) => {
                  return button.type !== 'PHONE_NUMBER' || !!value;
                }
              ),
              phoneFormat,
              maxLength: helpers.withMessage(
                'WHATSAPP_TEMPLATES.ADD.ERRORS.BUTTONS.PHONE_MAX_LENGTH_ERROR',
                maxLength(VALIDATION_CONSTANTS.MAX_PHONE_LENGTH)
              ),
            },
            example: {
              required: helpers.withMessage(
                'WHATSAPP_TEMPLATES.ADD.ERRORS.VALUE_REQUIRED_ERROR',
                (value, button) => {
                  if (button.type === 'URL' && button.hasVariable)
                    return !!value;
                  return true;
                }
              ),
              maxLength: helpers.withMessage(
                'WHATSAPP_TEMPLATES.ADD.ERRORS.BUTTONS.EXAMPLE_MAX_LENGTH_ERROR',
                maxLength(VALIDATION_CONSTANTS.MAX_BUTTON_TEXT)
              ),
            },
          }),
        },
      }),
    },
  };
});

const v$ = useVuelidate(rules, { cards });

async function validateCards() {
  await v$.value.$validate();
  return v$.value.cards.$invalid;
}

defineExpose({
  validateCards,
});

watch(
  cards,
  newCards => {
    v$.value.$touch();
    newCards.forEach(card => {
      card.buttons.forEach(btn => {
        if (btn.type === 'URL' && btn.url) {
          btn.hasVariable = btn.url.includes('{{1}}');
        }
      });
    });
    emit('update:modelValue', newCards);
  },
  { deep: true }
);
</script>

<template>
  <div class="mb-6">
    <!-- Cards List -->
    <div v-if="cards.length > 0" class="flex flex-col gap-4">
      <div
        v-for="(card, index) in cards"
        :key="card.id"
        class="border border-n-weak rounded-lg overflow-hidden bg-white dark:bg-n-solid-3"
      >
        <!-- Card Header -->
        <div
          class="flex items-center justify-between border-b border-n-weak py-2 px-3 bg-n-alpha-2"
        >
          <span class="font-medium text-sm">{{
            $t('WHATSAPP_TEMPLATES.ADD.CAROUSEL.CARD_LABEL', {
              index: index + 1,
            })
          }}</span>
          <Button
            variant="faded"
            color="ruby"
            size="sm"
            icon="i-lucide-trash"
            :disabled="cards.length <= VALIDATION_CONSTANTS.MIN_CAROUSEL_CARDS"
            @click="removeCard(index)"
          />
        </div>

        <div class="p-4 flex flex-col gap-4">
          <!-- Card Media Header -->
          <div class="flex flex-col gap-2">
            <div>
              <label class="mb-1">
                {{ $t('WHATSAPP_TEMPLATES.ADD.CAROUSEL.CARD_HEADER_LABEL') }}
              </label>
              <select v-model="card.headerType">
                <option value="IMAGE">
                  {{ $t('WHATSAPP_TEMPLATES.ADD.CAROUSEL.HEADER_TYPE_IMAGE') }}
                </option>
                <option value="VIDEO">
                  {{ $t('WHATSAPP_TEMPLATES.ADD.CAROUSEL.HEADER_TYPE_VIDEO') }}
                </option>
              </select>
            </div>

            <!-- Media Upload -->
            <div class="flex gap-4">
              <FileUpload
                :input-id="`card-media-${card.id}`"
                :size="MEDIA_CONFIG[card.headerType].maxSize"
                :accept="MEDIA_CONFIG[card.headerType].accept"
                :drop="false"
                @input-file="file => handleCardMediaUpload(file, index)"
              >
                <woot-button
                  :is-loading="card.isUploading"
                  class-names="button--upload"
                  icon="attach"
                  emoji="📎"
                  color-scheme="secondary"
                  variant="smooth"
                  size="small"
                >
                  {{
                    card.mediaFile
                      ? $t('WHATSAPP_TEMPLATES.ADD.CAROUSEL.CHANGE_FILE')
                      : $t('WHATSAPP_TEMPLATES.ADD.CAROUSEL.CHOOSE_FILE')
                  }}
                </woot-button>
              </FileUpload>

              <div
                v-if="card.mediaFile"
                class="mt-2 text-xs flex items-center gap-2"
              >
                <span>{{ card.mediaFile.name }}</span>
                <span class="text-n-slate-10"
                  >({{ formatFileSize(card.mediaFile.size) }})</span
                >
              </div>
            </div>
            <p
              v-if="v$.cards.$each.$response?.$errors[index]?.mediaFile?.length"
              class="text-xs text-n-ruby-9 mt-1"
            >
              {{
                $t(
                  v$.cards.$each.$response?.$errors[index]?.mediaFile[0]
                    .$message,
                  v$.cards.$each.$response?.$errors[index]?.mediaFile[0].$params
                )
              }}
            </p>
          </div>

          <!-- Card Body -->
          <Input
            v-model="card.body"
            :label="$t('WHATSAPP_TEMPLATES.ADD.CAROUSEL.CARD_BODY_LABEL')"
            :placeholder="
              $t('WHATSAPP_TEMPLATES.ADD.CAROUSEL.CARD_BODY_PLACEHOLDER')
            "
            :max-length="VALIDATION_CONSTANTS.MAX_CARD_BODY_TEXT"
            show-character-count
            :message-type="
              v$.cards.$each.$response?.$errors[index]?.body?.length
                ? 'error'
                : 'info'
            "
            :message="
              v$.cards.$each.$response?.$errors[index]?.body?.length
                ? $t(
                    v$.cards.$each.$response?.$errors[index]?.body[0].$message,
                    v$.cards.$each.$response?.$errors[index]?.body[0].$params
                  )
                : ''
            "
          />

          <!-- Card Buttons -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="text-xs font-medium text-n-slate-12">{{
                $t('WHATSAPP_TEMPLATES.ADD.CAROUSEL.BUTTONS_LABEL')
              }}</label>
              <Button
                v-if="card.buttons.length < 2"
                size="xs"
                variant="faded"
                color="blue"
                icon="i-lucide-plus"
                @click="addCardButton(index)"
              >
                {{ $t('WHATSAPP_TEMPLATES.ADD.CAROUSEL.ADD_BUTTON') }}
              </Button>
            </div>

            <div v-if="card.buttons.length > 0" class="flex flex-col gap-2">
              <div
                v-for="(button, btnIndex) in card.buttons"
                :key="btnIndex"
                class="flex gap-2 items-start"
              >
                <div
                  class="flex-1 grid gap-2"
                  :class="
                    button.hasVariable
                      ? 'grid-cols-2 md:grid-cols-4'
                      : 'grid-cols-1 md:grid-cols-3'
                  "
                >
                  <select v-model="button.type" class="w-full">
                    <option value="QUICK_REPLY">
                      {{ $t('WHATSAPP_TEMPLATES.ADD.BUTTONS.QUICK_REPLY') }}
                    </option>
                    <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                    <option value="URL">URL</option>
                    <option value="PHONE_NUMBER">
                      {{
                        $t('WHATSAPP_TEMPLATES.ADD.BUTTONS.PHONE_NUMBER_LABEL')
                      }}
                    </option>
                  </select>
                  <Input
                    v-model="button.text"
                    :placeholder="
                      $t(
                        'WHATSAPP_TEMPLATES.ADD.CAROUSEL.BUTTON_TEXT_PLACEHOLDER'
                      )
                    "
                    :max-length="VALIDATION_CONSTANTS.MAX_BUTTON_TEXT"
                    :message-type="
                      v$.cards.$each.$response?.$errors[index]?.buttons?.[0]
                        ?.$response?.$errors[btnIndex]?.text?.length
                        ? 'error'
                        : 'info'
                    "
                    :message="
                      v$.cards.$each.$response?.$errors[index]?.buttons?.[0]
                        ?.$response?.$errors[btnIndex]?.text?.length
                        ? $t(
                            v$.cards.$each.$response?.$errors[index]
                              ?.buttons?.[0]?.$response?.$errors[btnIndex]
                              ?.text[0]?.$message,
                            v$.cards.$each.$response?.$errors[index]
                              ?.buttons?.[0]?.$response?.$errors[btnIndex]
                              ?.text[0].$params
                          )
                        : ''
                    "
                  />
                  <template v-if="button.type !== 'QUICK_REPLY'">
                    <!-- URL or Phone inputs -->
                    <Input
                      v-if="button.type === 'URL'"
                      v-model="button.url"
                      :placeholder="
                        $t('WHATSAPP_TEMPLATES.ADD.CAROUSEL.URL_PLACEHOLDER')
                      "
                      :message-type="
                        v$.cards.$each.$response?.$errors[index]?.buttons?.[0]
                          ?.$response?.$errors[btnIndex]?.url?.length
                          ? 'error'
                          : 'info'
                      "
                      :message="
                        v$.cards.$each.$response?.$errors[index]?.buttons?.[0]
                          ?.$response?.$errors[btnIndex]?.url?.length
                          ? $t(
                              v$.cards.$each.$response?.$errors[index]
                                ?.buttons?.[0]?.$response?.$errors[btnIndex]
                                ?.url[0]?.$message,
                              v$.cards.$each.$response?.$errors[index]
                                ?.buttons?.[0]?.$response?.$errors[btnIndex]
                                ?.url[0].$params?.$each?.$response?.$errors[
                                btnIndex
                              ]?.url[0]?.$params
                            )
                          : ''
                      "
                    />
                    <Input
                      v-if="button.type === 'PHONE_NUMBER'"
                      v-model="button.phone_number"
                      :placeholder="
                        $t('WHATSAPP_TEMPLATES.ADD.CAROUSEL.PHONE_PLACEHOLDER')
                      "
                      :max-length="VALIDATION_CONSTANTS.MAX_PHONE_LENGTH"
                      :message-type="
                        v$.cards.$each.$response?.$errors[index]?.buttons?.[0]
                          ?.$response?.$errors[btnIndex]?.phone_number?.length
                          ? 'error'
                          : 'info'
                      "
                      :message="
                        v$.cards.$each.$response?.$errors[index]?.buttons?.[0]
                          ?.$response?.$errors[btnIndex]?.phone_number?.length
                          ? $t(
                              v$.cards.$each.$response?.$errors[index]
                                ?.buttons?.[0]?.$response?.$errors[btnIndex]
                                ?.phone_number[0]?.$message,
                              v$.cards.$each.$response?.$errors[index]
                                ?.buttons?.[0]?.$response?.$errors[btnIndex]
                                ?.phone_number[0].$params
                            )
                          : ''
                      "
                    />
                  </template>

                  <!-- Example field if URL has variable -->
                  <Input
                    v-if="button.type === 'URL' && button.hasVariable"
                    v-model="button.example"
                    :placeholder="
                      $t(
                        'WHATSAPP_TEMPLATES.ADD.BUTTONS.EXAMPLE_VALUE_PLACEHOLDER'
                      )
                    "
                    :message-type="
                      v$.cards.$each.$response?.$errors[index]?.buttons?.[0]
                        ?.$response?.$errors[btnIndex]?.example?.length
                        ? 'error'
                        : 'info'
                    "
                    :message="
                      v$.cards.$each.$response?.$errors[index]?.buttons?.[0]
                        ?.$response?.$errors[btnIndex]?.example?.length
                        ? $t(
                            v$.cards.$each.$response?.$errors[index]
                              ?.buttons?.[0]?.$response?.$errors[btnIndex]
                              ?.example[0]?.$message,
                            v$.cards.$each.$response?.$errors[index]
                              ?.buttons?.[0]?.$response?.$errors[btnIndex]
                              ?.example[0].$params
                          )
                        : ''
                    "
                  />
                </div>
                <Button
                  class="shrink-0"
                  variant="ghost"
                  color="ruby"
                  size="sm"
                  icon="i-lucide-trash"
                  @click="removeCardButton(index, btnIndex)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Card Button -->
    <div
      v-if="cards.length < VALIDATION_CONSTANTS.MAX_CAROUSEL_CARDS"
      class="mt-4 flex justify-center"
    >
      <Button color="blue" class="w-full" @click="addCard">
        <Icon icon="i-lucide-plus" class="mr-2" />
        {{ $t('WHATSAPP_TEMPLATES.ADD.CAROUSEL.ADD_CARD') }}
      </Button>
    </div>
  </div>
</template>
